import { z } from 'zod';
/**
 * 공통 환경변수 스키마.
 *
 * 앱별 env 검증은 이 베이스를 상속(merge)해서 확장하세요.
 * 부팅 시점에 parse()하여 fail-fast 보장.
 */
export const baseEnvSchema = z.object({
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
    /** Postgres 연결 문자열. 예: postgres://user:pass@localhost:5432/loop_dev */
    DATABASE_URL: z.string().url(),
    /**
     * Redis 연결 문자열 (legacy — Phase 3 Plan 03-02 부터 deprecated).
     *
     * Phase 3 Plan 03-02 부터 `REDIS_CACHE_URL` · `REDIS_PUBSUB_URL` · `REDIS_QUEUE_URL` 로
     * 분리됨. 본 필드는 legacy alias — `mapLegacyRedisUrl()` 이 누락된 3 URL 을 본 값으로
     * 자동 복제하며 `process.stderr` 에 1회 경고. Wave 1 에 제거 예정.
     *
     * Phase 3 이전 (Phase 1~2) 코드는 REDIS_URL 만으로 Redis 에 접근했기 때문에 하위 호환
     * 유지를 위해 optional 로 유지 — 단, realtimeEnvSchema 는 3 URL 이 required 이므로
     * REDIS_URL 만 주입된 경우에도 mapping 후 Zod 검증 통과.
     *
     * @deprecated Phase 3 Plan 03-02 부터 deprecated. Wave 1 에 제거.
     */
    REDIS_URL: z.string().url().optional(),
    /**
     * @since Phase 3 Plan 03-02 — 앱 캐시용 (ElastiCache · local 6379).
     *
     * Wave 0 에서 세션/튜플 캐시 (현재 미사용). Phase 4+ BullMQ 미사용 로직이 활용.
     */
    REDIS_CACHE_URL: z.string().url().optional(),
    /**
     * @since Phase 3 Plan 03-02 — Socket.IO Redis Streams adapter 전용 (local 6380).
     *
     * `@socket.io/redis-streams-adapter` 가 본 URL 로 `XADD`/`XREAD` 실행. AOF off 정책
     * (pubsub 은 영속 불필요) — docker-compose 에서 `--appendonly no` 설정.
     */
    REDIS_PUBSUB_URL: z.string().url().optional(),
    /**
     * @since Phase 3 Plan 03-02 — BullMQ backend (local 6381).
     *
     * Phase 4+ TTS 워커 · 지급대행 재시도 · 알림 큐가 본 URL 사용. AOF on 정책
     * (작업 영속 필요) — docker-compose 에서 `--appendonly yes`.
     */
    REDIS_QUEUE_URL: z.string().url().optional(),
    /** 세션·JWT 서명 키. 최소 32바이트. prod는 Coolify에서 주입. */
    SESSION_SECRET: z.string().min(32),
    /** 로그 레벨 */
    LOG_LEVEL: z.enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal']).default('info'),
});
let legacyWarnEmitted = false;
/**
 * REDIS_URL → REDIS_CACHE_URL · REDIS_PUBSUB_URL · REDIS_QUEUE_URL 자동 mapping.
 *
 * 정책 (Phase 3 CONTEXT §decisions "Redis 3-split"):
 *  - 3 URL 중 1개라도 누락 + REDIS_URL 이 존재 → REDIS_URL 값으로 자동 복제 + stderr 경고 1회
 *  - 3 URL 전부 누락 + REDIS_URL 도 없음 → pass-through (realtimeEnvSchema 가 superRefine 으로 reject)
 *  - 3 URL 모두 주입되었으면 REDIS_URL 무시 (경고 없음)
 *
 * 용도: Wave 0 phase 1/2 의 기존 REDIS_URL 기반 설정과의 하위 호환 유지.
 * Wave 1 에 제거 예정 — 본 함수를 호출하지 않고 Zod 가 바로 reject.
 *
 * Side-effect 주의: `process.stderr` 에 경고를 출력 (1회만). `__resetLegacyWarnForTesting()` 으로
 * test 간 격리.
 */
export function mapLegacyRedisUrl(source) {
    const out = { ...source };
    const legacy = out.REDIS_URL;
    const keys = ['REDIS_CACHE_URL', 'REDIS_PUBSUB_URL', 'REDIS_QUEUE_URL'];
    const missing = keys.filter((k) => !out[k]);
    if (legacy && missing.length > 0) {
        for (const k of missing)
            out[k] = legacy;
        if (!legacyWarnEmitted) {
            process.stderr.write(`⚠️  REDIS_URL is deprecated since Phase 3. Auto-mapped ${missing.join(', ')} from REDIS_URL. Scheduled for removal in Wave 1 — please inject 3 URLs explicitly.\n`);
            legacyWarnEmitted = true;
        }
    }
    return out;
}
/** Testing 전용 — mapLegacyRedisUrl 의 1회 경고 플래그 reset. */
export function __resetLegacyWarnForTesting() {
    legacyWarnEmitted = false;
}
/**
 * API 서버 전용 env.
 *
 * Phase 3 Plan 03-02: `REDIS_CACHE_URL` · `REDIS_PUBSUB_URL` · `REDIS_QUEUE_URL` 3 URL 을
 * optional 로 상속. apps/api 는 본 plan 에서 배선 안 하지만 Plan 03-03 의 broadcast helper
 * (apps/api → redis-pubsub XADD) 가 `REDIS_PUBSUB_URL` 사용 예정이므로 스키마에만 선 반영.
 */
export const apiEnvSchema = baseEnvSchema.extend({
    PORT: z.coerce.number().int().positive().default(4000),
    WEB_ORIGIN: z.string().url().default('http://localhost:3000'),
    WIDGET_ORIGIN: z.string().url().default('http://localhost:5173'),
    /**
     * Widget JWT HS256 서명 키 — Phase 5 / Plan 05-01.
     *
     * apps/api WidgetTokenService 가 본 키로 JWT 서명, apps/realtime widgetJwtMiddleware 는
     * 동일 키로 검증. 두 앱이 같은 값을 공유해야 함 (dev 는 .env 파일 공용, prod 는 Coolify
     * env 주입).
     *
     * 최소 32 chars — HS256 권장 엔트로피. 생성:
     *   `openssl rand -hex 32`  또는  `node -e "console.log(require('crypto').randomBytes(32).toString('hex'))"`.
     *
     * TTL 정책: JWT exp = 10년 (effectively "no expiry"), revocation 은 jti 기반
     * (`widget:revoked-jti` Redis SET + widget_tokens.revoked_at DB fallback).
     *
     * @since Phase 5 Plan 05-01 — default __PENDING_PHASE5__ 32-char padding 은 dev 편의용,
     *   prod 는 반드시 주입.
     */
    WIDGET_JWT_SECRET: z
        .string()
        .min(32, 'WIDGET_JWT_SECRET must be >=32 chars for HS256')
        .default('__PENDING_PHASE5_WIDGET_JWT_SECRET_'),
    /**
     * Widget 공개 URL 기저 — issueToken 응답의 `url` 필드 조립에 사용.
     *
     * 포맷: `${WIDGET_PUBLIC_URL}/widget?streamerId=<uuid>&token=<jwt>` — DESIGN.md §3 계약.
     *
     * Wave 0 dev: `http://localhost:5173` (Vite widget app) 또는 apps/realtime
     *             (`http://localhost:4100`) — 배포 환경에 따라 선택.
     * Prod: `https://widget.loop.live` 또는 `https://realtime.loop.live`.
     *
     * @since Phase 5 Plan 05-01
     */
    WIDGET_PUBLIC_URL: z.string().url().default('http://localhost:5173'),
    /**
     * Toss Payments 시크릿 키 — Basic 인증 헤더로 REST API 호출 시 사용.
     * 출처: Toss 개발자센터 > 애플리케이션 > API 키 > 시크릿 키 (test_sk_* / live_sk_*).
     *
     * @deprecated Phase 4 에서 default 제거 예정 — 실제 Toss 연동 시 prod 에 반드시 주입.
     *   `__PENDING_PHASE4__` 기본값은 Wave 0 phase 1 범위 (실제 Toss 호출 코드 없음) 에서만 유효.
     */
    TOSS_SECRET_KEY: z.string().min(1).default('__PENDING_PHASE4__'),
    /**
     * Toss 지급대행 JWE 암호화 보안키 — Payout API request body 암호화 (A256KW + A256GCM) 에 사용.
     * 출처: Toss 개발자센터 > 지급대행 > 보안키.
     *
     * @deprecated Phase 4 에서 default 제거 예정 — 실제 지급대행 연동 시 prod 에 반드시 주입.
     */
    TOSS_PAYOUT_SECURITY_KEY: z.string().min(1).default('__PENDING_PHASE4__'),
    /**
     * Toss 웹훅 시크릿 — 수신 webhook 의 서명 검증에 사용.
     * 출처: Toss 개발자센터 > 웹훅 > 시크릿.
     *
     * @deprecated Phase 4 에서 default 제거 예정 — webhook 엔드포인트 구현 시점에 prod 에 주입.
     */
    TOSS_WEBHOOK_SECRET: z.string().min(1).default('__PENDING_PHASE4__'),
    /**
     * Fan 측 수수료율 (basis points). 10% = 1000 bps (PAY-06 기본값).
     *
     * 계산: `fee_fan = base * FEE_FAN_BPS / 10000` (반올림). 팬 결제 총액은
     * `amount_gross = base + fee_fan` (gross-up 방식 — "응원 프리미엄" 프레이밍).
     *
     * 운영 환경에서 변경 가능하나 ENG-REVIEW 결정 시까지 default 고정.
     *
     * @since Phase 4 Plan 04-04
     */
    FEE_FAN_BPS: z.coerce.number().int().min(0).max(10000).default(1000),
    /**
     * Streamer 측 수수료율 (basis points). 10% = 1000 bps (PAY-06 기본값).
     *
     * 계산: `fee_streamer = base * FEE_STREAMER_BPS / 10000` (반올림).
     * 스트리머 순지급액은 `amount_net = base - fee_streamer`.
     *
     * @since Phase 4 Plan 04-04
     */
    FEE_STREAMER_BPS: z.coerce.number().int().min(0).max(10000).default(1000),
    /**
     * Toss PARTIALLY_APPROVED 스트리머의 주당 지급 한도 (원).
     *
     * Toss 정책상 전화 본인인증만 마친 셀러는 주당 10,000,000원 미만까지만 지급 가능.
     * FULLY_APPROVED 셀러는 본 한도 적용 대상 아님 (무제한).
     *
     * bigint 로 저장 — 금액 비교 시 JavaScript 정수 안정성 (2^53) 초과 방지.
     * 기본값 10_000_000 은 Toss 공식 한도 (2026-04 기준).
     *
     * @since Phase 4 Plan 04-05
     */
    TOSS_PAYOUT_PARTIAL_WEEKLY_LIMIT_KRW: z.coerce
        .bigint()
        .default(10000000n),
    /**
     * 지급대행 실패 시 알림을 보낼 운영팀 이메일.
     *
     * Wave 0 은 Logger.warn 으로만 기록 (PayoutWebhookHandler), Wave 1 에 nodemailer
     * + SES 배선으로 실제 발송. 본 env 값은 "누구에게 보낼지" 만 결정 — 실발송 로직은
     * 별도 plan.
     *
     * @since Phase 4 Plan 04-05
     */
    PAYOUT_FAILURE_NOTIFY_EMAIL: z.string().email().default('ops@loop.test'),
    /**
     * Wave 0 임시 admin user id whitelist (쉼표 구분 UUID).
     *
     * Plan 04-05 PayoutController 의 `POST /api/v1/payouts/:streamerId/execute` 가
     * `ADMIN_USER_IDS.includes(req.user.id)` 로 403 판정. Wave 1 정식 RBAC
     * (`users.role` 컬럼 혹은 `app_admins` 테이블) 로 교체.
     *
     * 빈 문자열 → 빈 배열 → 모든 요청이 403 (안전 fail-closed). 본 placeholder 는
     * dev 환경에서 env 에 명시적으로 주입하도록 강제.
     *
     * @since Phase 4 Plan 04-05
     */
    ADMIN_USER_IDS: z
        .string()
        .default('')
        .transform((s) => s
        ? s
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean)
        : []),
    // ---- Reconciliation (Plan 04-06) ----
    /**
     * ReconciliationCron 활성화 flag — 매일 KST 06:00 (UTC 21:00) 자동 실행.
     *
     * `false` 일 때 onApplicationBootstrap 에서 cron 등록 skip — 테스트·부분 배포 환경에서
     * cron 을 비활성화하면서 app 전체 기동은 유지. production 은 기본 true.
     *
     * @since Phase 4 Plan 04-06
     */
    RECONCILIATION_CRON_ENABLED: z
        .string()
        .default('true')
        .transform((s) => s === 'true' || s === '1'),
    /**
     * Toss 정산 리포트 vs 내부 ledger SUM 의 |diff| 허용 임계값 (원).
     *
     * `|toss - ledger| > threshold` 일 때만 discrepancy 로 기록. 반올림 오차나 timestamp
     * 경계 차이로 인한 소액 불일치는 누적해서 수동 처리 — Wave 0 기본 100원.
     *
     * bigint 저장 — JS Number 안정성 (2^53) 초과 방지.
     *
     * @since Phase 4 Plan 04-06
     */
    RECONCILIATION_DIFF_THRESHOLD_KRW: z.coerce.bigint().default(100n),
    /**
     * 일배치 불일치 발견 시 알림을 수신할 ops 이메일.
     *
     * NotificationService.sendDiscrepancyAlert 의 To 주소. PayoutService 실패 알림용
     * PAYOUT_FAILURE_NOTIFY_EMAIL 과 분리 — 동일 주소로 세팅해도 무방하나 목적별 라우팅 용.
     *
     * @since Phase 4 Plan 04-06
     */
    RECONCILIATION_ALERT_EMAIL: z.string().email().default('ops@loop.test'),
    /**
     * NotificationService transport driver.
     *
     * - `mailhog` (dev default) — localhost:1025 SMTP (docker-compose.dev.yml mailhog 서비스).
     * - `ses` (prod) — AWS SES (SESClient + SendEmailCommand). `AWS_SES_REGION` + `AWS_SES_SENDER`
     *   동시 주입 필요. AWS credentials 는 SDK 표준 chain (IAM role / env / profile).
     * - `noop` (test · CI) — 실전송 없이 `{messageId: 'noop-<ts>'}` 반환.
     *
     * 본 값은 04-05 PayoutService 의 email stub 알림도 포함 — Wave 1 에 PayoutWebhookHandler
     * 가 NotificationService 를 직접 호출하도록 교체 예정 (04-06 에서 인터페이스 정비).
     *
     * @since Phase 4 Plan 04-06
     */
    EMAIL_TRANSPORT: z.enum(['mailhog', 'ses', 'noop']).default('mailhog'),
    /**
     * SES region — `EMAIL_TRANSPORT='ses'` 일 때만 사용. 그 외는 무시.
     *
     * AWS 정책: SES sending 은 verified region 내에서만 유효. 한국 리전 (ap-northeast-2)
     * default — 도쿄 (ap-northeast-1) 또는 US East (us-east-1) 로 override 가능.
     *
     * @since Phase 4 Plan 04-06
     */
    AWS_SES_REGION: z.string().default('ap-northeast-2'),
    /**
     * SES + SMTP 모두 공통 sender ("From" 주소).
     *
     * SES: 반드시 SES console 에서 verified identity (도메인 또는 이메일) 여야 송신 성공.
     * Mailhog: 제약 없음 (테스트 서버 — 모든 도메인 수용).
     *
     * @since Phase 4 Plan 04-06
     */
    AWS_SES_SENDER: z.string().email().default('noreply@loop.live'),
    /**
     * Mailhog / SES SMTP host — NotificationService MailhogTransport 가 사용.
     *
     * dev 기본: 'localhost' (docker-compose mailhog 서비스가 1025 포트 노출).
     * prod: SES SMTP endpoint (`email-smtp.ap-northeast-2.amazonaws.com`) — 단, 본 plan
     * 은 SES 는 API (SDK) 경유라 MailhogTransport 가 아닌 SesTransport 분기를 타므로
     * SMTP host 는 dev Mailhog 전용.
     *
     * @since Phase 4 Plan 04-06
     */
    EMAIL_SERVER_HOST: z.string().default('localhost'),
    /**
     * Mailhog SMTP port — 기본 1025 (docker-compose.dev.yml 에 1025:1025 매핑).
     *
     * @since Phase 4 Plan 04-06
     */
    EMAIL_SERVER_PORT: z.coerce.number().int().positive().default(1025),
    /**
     * Ledger Integrity Probe 의 default transactions 수 — Plan 04-07 Task 3.
     *
     * `scripts/ledger-integrity-probe.mjs` 는 CLI `--transactions` 또는 본 env 로
     * 거래 수를 조정 가능. 기본 10_000 (PAY-03 CI gate 기준). local 디버깅 시 100 등
     * 작은 값으로 빠르게 검증.
     *
     * @since Phase 4 Plan 04-07
     */
    LEDGER_PROBE_DEFAULT_TRANSACTIONS: z.coerce
        .number()
        .int()
        .positive()
        .default(10000),
    // ---- Phase 5 Plan 05-03 — CLOVA Voice TTS + BullMQ ----
    /**
     * CLOVA Voice API Key ID — `X-NCP-APIGW-API-KEY-ID` 헤더 값.
     *
     * 출처: Naver Cloud Platform console → AI·NAVER API → CLOVA Voice → Application
     * registration → API Gateway → Key ID.
     *
     * dev/test: `__PENDING_PHASE5_PLAN03__` placeholder 허용. prod 에서 실 주입 시
     * CLOVA 호출이 활성화됨. placeholder 상태에서 호출 시 401 → auth_failed 에러.
     *
     * @since Phase 5 Plan 05-03 — TTS 합성 (WIDGET-03).
     */
    CLOVA_VOICE_KEY_ID: z.string().min(1).default('__PENDING_PHASE5_PLAN03__'),
    /**
     * CLOVA Voice API Key (Secret) — `X-NCP-APIGW-API-KEY` 헤더 값.
     *
     * 출처: 동일 애플리케이션 설정 페이지. 시크릿 값이므로 절대 로그·에러 메시지에
     * 포함 금지 (T-05-14 Information Disclosure 완화).
     *
     * @since Phase 5 Plan 05-03
     */
    CLOVA_VOICE_KEY: z.string().min(1).default('__PENDING_PHASE5_PLAN03__'),
    /**
     * CLOVA Voice 기본 speaker (voice preset).
     *
     * 기본 `nara` — DESIGN.md §3.3 `classic` 프리셋 (여성·차분). theme.json 의
     * `soundPreset` 이 주어지면 TTS 워커에서 per-donation override 가능.
     *
     * 사용 가능 값: `nara`, `jinho`, `dsinu-matt`, `mijin`, `clara`, `nminjun` 등
     * (CLOVA Voice 공식 문서 참조).
     *
     * @since Phase 5 Plan 05-03
     */
    CLOVA_VOICE_SPEAKER_DEFAULT: z.string().default('nara'),
    /**
     * TTS 텍스트 최대 글자 수 — 금액 폭탄 방어 (T-05-16 Financial Abuse).
     *
     * 기본 200자 — donation.completed message 길이 상한 (packages/contracts widget
     * toast schema 의 message.max(200) 과 일치). 초과 시 enqueue 하지 않고 즉시
     * `donation.tts_failed.v1` (reason='text_too_long') 발행.
     *
     * Plan 05-04 욕설 필터가 앞단에서 1차 걸러내므로 실효 길이는 더 짧음.
     *
     * @since Phase 5 Plan 05-03
     */
    TTS_MAX_CHARS: z.coerce.number().int().positive().default(200),
    /**
     * TTS audio Redis 캐시 TTL (초).
     *
     * CLOVA "저장 금지" 정책 (PITFALLS / 05-CONTEXT) — disk · S3 저장 금지, 메모리
     * (Redis) 단기 캐시만 허용. 기본 300초 (5분) — 위젯이 broadcast 수신 후 audio fetch
     * 까지의 여유 + 단기 재연결 retry 대응.
     *
     * @since Phase 5 Plan 05-03
     */
    TTS_CACHE_TTL_SEC: z.coerce.number().int().positive().default(300),
    /**
     * TTS audio public URL base — broadcast payload `audioUrl` 조립에 사용.
     *
     * 포맷: `${TTS_PUBLIC_URL}/api/v1/tts/${ttsId}`. dev 기본 apps/api (`http://localhost:4000`).
     * prod: `https://api.loop.live`.
     *
     * apps/widget 이 본 prefix 로 시작하는 URL 만 신뢰하도록 Plan 05-05 에서 검증
     * (T-05-17 Spoofing audioUrl).
     *
     * @since Phase 5 Plan 05-03
     */
    TTS_PUBLIC_URL: z.string().url().default('http://localhost:4000'),
    // ---- Phase 5 Plan 05-03 — 비즈엠 Alimtalk (LOOP 대행 발송) ----
    /**
     * 비즈엠 Alimtalk API base URL — 기본 `https://alimtalk-api.bizmsg.kr`.
     *
     * 테스트·샌드박스 환경에서 override 가능. 실 prod 는 default 고정.
     *
     * @since v1.1 Phase 5 Plan 05-03
     */
    BIZM_BASE_URL: z.string().url().default('https://alimtalk-api.bizmsg.kr'),
    /**
     * 비즈엠 `userid` 헤더 — LOOP 법인 계약 시 발급.
     *
     * BIZM_DRY_RUN=true (Wave 0 default) 경로에서는 사용되지 않음. OPS-W1-02 계약 완료
     * 후 prod env 에 주입.
     *
     * @since v1.1 Phase 5 Plan 05-03
     */
    BIZM_USER_ID: z.string().min(1).optional(),
    /**
     * 비즈엠 발신 프로필 키 — LOOP 법인 카카오 알림톡 발신 프로필.
     *
     * 본 키로 등록된 템플릿만 사용 가능 (alimtalk_templates.template_code 매핑).
     * OPS-W1-02 계약 단계에서 발급 → prod env 주입.
     *
     * @since v1.1 Phase 5 Plan 05-03
     */
    BIZM_PROFILE_KEY: z.string().min(1).optional(),
    /**
     * 비즈엠 API key (일부 계약 형태에서 요구). 사용 안 해도 허용.
     *
     * @since v1.1 Phase 5 Plan 05-03
     */
    BIZM_API_KEY: z.string().min(1).optional(),
    /**
     * Dry-run flag — true 이면 실 네트워크 호출 없이 fake response 반환.
     *
     * Wave 0 기본 `true` — OPS-W1-02 계약 + 비즈엠 승인 전까지 발송 차단.
     * prod 에서 계약 완료 후 `false` 로 전환 + BIZM_USER_ID/PROFILE_KEY 주입.
     *
     * @since v1.1 Phase 5 Plan 05-03
     */
    BIZM_DRY_RUN: z
        .enum(['true', 'false'])
        .default('true')
        .transform((s) => s === 'true'),
    /**
     * 비즈엠 fetch timeout (ms). 기본 10000 (10초).
     *
     * CircuitBreaker 가 외부 장애 연쇄 차단 — T-05-03-03 DoS mitigation.
     *
     * @since v1.1 Phase 5 Plan 05-03
     */
    BIZM_TIMEOUT_MS: z.coerce.number().int().positive().default(10_000),
    // ---- Phase 5 Plan 05-04 — Naver Clean Bot (욕설 필터 Layer 3) ----
    /**
     * Clean Bot 활성화 flag.
     *
     * Wave 0 기본 `false` — 자체 사전 (global + streamer) 만으로 운영 가능. 본 plan 완성 후
     * ops 가 true 전환하여 Naver Clean Bot layer 를 활성화. ENABLED=true 일 때만
     * `CLEAN_BOT_KEY_ID` / `CLEAN_BOT_KEY` 가 실호출에 사용됨.
     *
     * @since Phase 5 Plan 05-04
     */
    CLEAN_BOT_ENABLED: z
        .string()
        .default('false')
        .transform((s) => s === 'true' || s === '1'),
    /**
     * Clean Bot API Key ID (`X-NCP-APIGW-API-KEY-ID`).
     *
     * NCP Clean Bot application 등록 시 발급. `CLEAN_BOT_ENABLED=true` 면 실주입 필수 —
     * placeholder 상태에서 호출 시 401 auth_failed 로 soft-fail (degraded=true).
     *
     * @since Phase 5 Plan 05-04
     */
    CLEAN_BOT_KEY_ID: z.string().optional(),
    /**
     * Clean Bot API Secret Key (`X-NCP-APIGW-API-KEY`).
     *
     * 시크릿 값 — 로그·에러 메시지 유출 금지 (T-05-22).
     *
     * @since Phase 5 Plan 05-04
     */
    CLEAN_BOT_KEY: z.string().optional(),
    /**
     * Clean Bot REST endpoint. NCP 공식 docs 기본값.
     * Wave 1 에 endpoint 변경 가능성 대응 — env override.
     *
     * @since Phase 5 Plan 05-04
     */
    CLEAN_BOT_ENDPOINT: z
        .string()
        .url()
        .default('https://naveropenapi.apigw.ntruss.com/clean-bot/v1/text'),
    /**
     * Clean Bot 호출 timeout (ms). 기본 3000 (3초).
     *
     * Timeout 발생 시 soft-fail (safe=true + degraded=true) — 자체 사전이 이미 실행된 후이므로
     * 안전 default. T-05-23 DoS 완화.
     *
     * @since Phase 5 Plan 05-04
     */
    CLEAN_BOT_TIMEOUT_MS: z.coerce.number().int().positive().default(3000),
    /**
     * `POST /api/v1/widgets/me/simulate-donation` 활성화 flag — Plan 06-04 Task 1.
     *
     * - `NODE_ENV==='production'` 이고 본 값이 `'true'` 가 아니면 controller 가 403
     *   `simulation_disabled` 반환 (T-06-04-09).
     * - dev/test/alpha 환경에서는 기본 허용 (NODE_ENV 조건으로 env 무시).
     * - 알파 스트리머 스테이징 배포에서 명시적으로 `true` 주입 권장 (PHASE-06-ENV.md
     *   에서 문서화 — PLAN-06-05 에서 작성).
     *
     * @since Phase 6 Plan 06-04
     */
    ALLOW_SIMULATE_DONATION: z
        .enum(['true', 'false'])
        .default('false'),
    /**
     * platform_connections.access_token / refresh_token 애플리케이션 레벨 AES-256-GCM 키.
     *
     * 32 bytes = 64 hex chars 강제. 생성:
     *   `openssl rand -hex 32`
     *   또는 `node -e "console.log(require('crypto').randomBytes(32).toString('hex'))"`
     *
     * Coolify (또는 배포 환경) 에서 prod env 로 주입. 본 키 유출 시 모든 platform OAuth
     * 토큰이 복호화 가능 — 즉시 rotate + platform_connections 재인증 유도 필요.
     *
     * 키 회전 (Wave 2 KMS): 본 plan 은 single-key. KMS 이행 시 `keyId` prefix 추가 예정.
     *
     * Phase 4 revert 대상 아님 — Phase 2 부터 실제 사용 (default 금지).
     *
     * 근거: `.planning/phases/02-identity-platform-connector/02-CONTEXT.md` §decisions
     *       "OAuth 토큰 보관" + PITFALLS.md §P13.
     */
    PLATFORM_TOKEN_ENCRYPTION_KEY: z
        .string()
        .regex(/^[0-9a-fA-F]{64}$/, 'must be 64 hex chars (32 bytes)'),
    /**
     * subscriptions.billing_key_enc 애플리케이션 레벨 AES-256-GCM 키 — v1.1 Phase 2 / Plan 02-01.
     *
     * 용도:
     *   Plan 02-02 SubscriptionService 가 Toss 빌링키 발급 직후
     *   `encryptToken(billingKey, env.SUBSCRIPTION_BILLING_KEY_ENCRYPTION_KEY)` 로 암호화하여
     *   subscriptions.billing_key_enc bytea 컬럼에 저장. Plan 02-03 BillingCron 이 매 갱신 시
     *   `decryptToken(row.billingKeyEnc, ...)` 로 복호화하여 Toss `/v1/billing/:billingKey` 호출.
     *
     * 32 bytes = 64 hex chars 강제 (PLATFORM_TOKEN_ENCRYPTION_KEY 와 동일 정책 — default 금지).
     * 생성:
     *   `openssl rand -hex 32`
     *   또는 `node -e "console.log(require('crypto').randomBytes(32).toString('hex'))"`
     *
     * 보안:
     *   - 본 키 유출 시 모든 subscriptions.billing_key_enc 가 복호화 가능 →
     *     팬 카드 무제한 청구 가능 (T-02-01-01 Information Disclosure, High impact).
     *   - 유출 감지 시 즉시 rotate + 전 subscriptions 재암호화 migration + 영향 팬 통지.
     *   - Wave 2 KMS 이행 시 `keyId` prefix (1 byte version) 추가 예정.
     *   - PLATFORM_TOKEN_ENCRYPTION_KEY 와는 **반드시 별도 키** — 키 분리 원칙
     *     (OAuth 토큰 유출과 billingKey 유출의 blast radius 분리).
     *
     * Coolify (또는 배포 환경) 에서 prod env 로 주입. default 금지 → Plan 02-02 배선 즉시
     * 실주입 필수. dev/test 에서는 `a`.repeat(64) placeholder 사용 (env.test.ts 참조).
     *
     * 근거:
     *   - `.planning/phases/02-subscription/02-CONTEXT.md` §decisions "Schema · billing_key_enc"
     *   - `.planning/phases/02-subscription/02-01-PLAN.md` <threat_model> T-02-01-01
     *   - PITFALLS.md §P13 — 개인정보 / 결제 식별자 암호화 at rest
     *
     * @since v1.1 Phase 2 Plan 02-01
     */
    SUBSCRIPTION_BILLING_KEY_ENCRYPTION_KEY: z
        .string()
        .regex(/^[0-9a-fA-F]{64}$/, 'must be 64 hex chars (32 bytes)'),
    /**
     * Auth.js v5 세션·CSRF 토큰 서명에 사용. 최소 32 chars.
     * 생성: `openssl rand -base64 32` 또는 `openssl rand -hex 32`.
     * Phase 2 Auth.js 배선 (Plan 02-02) 에서 실제 사용.
     *
     * @deprecated Plan 02-02 에서 default 제거 예정 — Auth.js 배선 시 prod 주입.
     *   default 는 zod `.min(32)` 검증을 통과해야 하므로 32 chars 로 padding.
     */
    AUTH_SECRET: z.string().min(32).default('__PENDING_PHASE2_PLAN02_AUTH_____'),
    // ---- OAuth providers (Plan 02-02 에서 실사용, 본 plan 은 scaffold) ----
    /** 카카오 OAuth client id — Kakao Developers. Plan 02-02. */
    KAKAO_CLIENT_ID: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    /** 카카오 OAuth client secret — Kakao Developers. Plan 02-02. */
    KAKAO_CLIENT_SECRET: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    /** 네이버 OAuth client id — Naver Developers. Plan 02-02. */
    NAVER_CLIENT_ID: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    /** 네이버 OAuth client secret — Naver Developers. Plan 02-02. */
    NAVER_CLIENT_SECRET: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    /** 구글 OAuth client id — Google Cloud Console. Plan 02-02. */
    GOOGLE_CLIENT_ID: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    /** 구글 OAuth client secret — Google Cloud Console. Plan 02-02. */
    GOOGLE_CLIENT_SECRET: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    /** 치지직 OAuth client id — 치지직 Developer Console. Plan 02-03. */
    CHZZK_CLIENT_ID: z.string().min(1).default('__PENDING_PHASE2_PLAN03__'),
    /** 치지직 OAuth client secret — 치지직 Developer Console. Plan 02-03. */
    CHZZK_CLIENT_SECRET: z.string().min(1).default('__PENDING_PHASE2_PLAN03__'),
    /**
     * 치지직 OAuth callback redirect URI — 치지직 Developer Console 에서 등록한 URI 와 정확히 일치해야 함.
     *
     * 개발 기본값: `http://localhost:4000/api/v1/connections/chzzk/callback`
     * 프로덕션: `https://api.loop.live/api/v1/connections/chzzk/callback` (Coolify env 주입)
     *
     * 본 값은 `buildAuthorizationUrl` 에서 query 로 나가고 callback 검증 시 비교되지 않음
     * (치지직이 검증) — LOOP 서버는 state HMAC 검증이 주 방어선.
     */
    CHZZK_REDIRECT_URI: z
        .string()
        .url()
        .default('http://localhost:4000/api/v1/connections/chzzk/callback'),
    // ---- SOOP (구 아프리카TV) OAuth (Phase 6 Plan 06-01) ----
    /**
     * SOOP OpenAPI client id — SOOP Developer Portal (https://developers.sooplive.co.kr)
     * 에서 파트너십 승인 후 발급받는 값.
     *
     * OPS-W1-04 (SOOP 파트너십 승인) 전까지 default `__PENDING_PHASE6_PLAN01__` 로 유지 —
     * SoopService.startConnect 가 default 감지 시 명시적 throw (실 배선 전 차단).
     * Plan 06-04 에서 실 값으로 교체 + 체크리스트 closure.
     *
     * @since Phase 6 Plan 06-01
     */
    SOOP_CLIENT_ID: z.string().min(1).default('__PENDING_PHASE6_PLAN01__'),
    /**
     * SOOP OpenAPI client secret — SOOP Developer Portal 에서 파트너십 승인 후 발급.
     *
     * 시크릿 값 — 로그·에러 메시지 유출 금지 (T-06-01-07 Information Disclosure).
     *
     * @since Phase 6 Plan 06-01
     */
    SOOP_CLIENT_SECRET: z.string().min(1).default('__PENDING_PHASE6_PLAN01__'),
    /**
     * SOOP OAuth callback redirect URI — SOOP Developer Console 에 등록된 URI 와
     * 정확히 일치해야 함 (SOOP 측 검증).
     *
     * 개발 기본값: `http://localhost:4000/api/v1/connections/soop/callback`
     * 프로덕션: `https://api.loop.live/api/v1/connections/soop/callback` (Coolify env 주입)
     *
     * @since Phase 6 Plan 06-01
     */
    SOOP_REDIRECT_URI: z
        .string()
        .url()
        .default('http://localhost:4000/api/v1/connections/soop/callback'),
    // ---- YouTube (Google) OAuth + Data API v3 (Phase 6 Plan 06-02) ----
    /**
     * YouTube Google OAuth client id — Google Cloud Console → APIs & Services →
     * Credentials → OAuth 2.0 Client ID 에서 발급.
     *
     * Wave 0 알파 배포 전까지 default `__PENDING_PHASE6_PLAN02__` 로 유지 —
     * YoutubeService.startConnect 가 default 감지 시 명시적 throw (실 배선 전 차단).
     * Plan 06-04 OPS 체크리스트 closure 후 실 값으로 교체.
     *
     * 주의: Phase 2 Plan 02-02 에 이미 `GOOGLE_CLIENT_ID` (Auth.js user login 용) 가 존재.
     *       두 클라이언트는 **별도 OAuth application** — scope / redirect URI 가 다르다.
     *
     * @since Phase 6 Plan 06-02
     */
    YOUTUBE_CLIENT_ID: z.string().min(1).default('__PENDING_PHASE6_PLAN02__'),
    /**
     * YouTube Google OAuth client secret — 동일 OAuth 2.0 Client 에서 발급.
     *
     * 시크릿 값 — 로그·에러 메시지 유출 금지 (T-06-02-04 Information Disclosure).
     *
     * @since Phase 6 Plan 06-02
     */
    YOUTUBE_CLIENT_SECRET: z.string().min(1).default('__PENDING_PHASE6_PLAN02__'),
    /**
     * YouTube OAuth callback redirect URI — Google Cloud Console 의 Authorized redirect URIs
     * 목록에 정확히 일치하도록 등록해야 함 (Google 측 strict 검증).
     *
     * 개발 기본값: `http://localhost:4000/api/v1/connections/youtube/callback`
     * 프로덕션:    `https://api.loop.live/api/v1/connections/youtube/callback`
     *
     * @since Phase 6 Plan 06-02
     */
    YOUTUBE_REDIRECT_URI: z
        .string()
        .url()
        .default('http://localhost:4000/api/v1/connections/youtube/callback'),
    /**
     * YouTube Data API Key (API Key credential) — QuotaTracker fallback / 일부 공개 읽기 호출.
     *
     * 본 plan 은 OAuth user token 을 primary 로 사용하므로 대부분의 호출에 사용되지 않으나,
     * Plan 06-04 의 일부 공개 정보 조회(검색 · 공개 채널 메타) 에서 activation 예정.
     *
     * @since Phase 6 Plan 06-02
     */
    YOUTUBE_API_KEY: z.string().min(1).default('__PENDING_PHASE6_PLAN02__'),
    /**
     * YouTube Data API v3 일일 quota 예산 (units).
     *
     * Google 기본 할당: 10,000 units/day. 각 API 호출 비용 (2026-01 revision):
     *   - channels.list       1
     *   - liveBroadcasts.list 1
     *   - superChatEvents.list 5
     *   - search.list         100 (사용 금지 — 폭주 방지)
     *
     * YoutubeQuotaTracker 가 Redis counter `youtube:quota:{YYYYMMDD}` 에 INCRBY 방식으로
     * 누적 기록. 80% 도달 시 warn 로그, 100% 도달 시 QuotaExceededError throw + 호출 block.
     *
     * PLAT-04 (quota 상향 100K+) 신청은 Plan 06-04 OPS 체크리스트에서 closure — 승인 후
     * 본 env 를 `100000` 등으로 override.
     *
     * @since Phase 6 Plan 06-02
     */
    YOUTUBE_DAILY_QUOTA_LIMIT: z.coerce.number().int().positive().default(10_000),
    // ---- PASS 본인인증 (Plan 02-04) ----
    /**
     * PASS 본인인증 provider selector.
     *
     * Wave 0: `'mock'` (dev/test 전용 PassMockClient).
     * Wave 0 종반: `'kcb'` 또는 `'nice'` — 실 계약 후 real client 구현.
     *
     * `prod` NODE_ENV 에서 `'mock'` 이 세팅되면 KycService constructor 가
     * 명시적으로 fail (T-02-04-01 Spoofing 완화).
     *
     * 근거: 02-CONTEXT.md §decisions "PASS 본인인증 — Wave 0 은 인터페이스 + mock 까지
     *       배선, 실 계약은 Wave 0 종반 OPS item"
     */
    PASS_PROVIDER: z.enum(['mock', 'kcb', 'nice']).default('mock'),
    /**
     * PASS client id — 실 계약 후 KCB 또는 NICE 발급값.
     * mock 환경에서는 사용되지 않으나 default 로 검증 통과 보장.
     *
     * @deprecated Plan 02-04 에서 default (`__PENDING_PHASE2_MOCK__`) 허용 — 실 계약 시 제거.
     */
    PASS_CLIENT_ID: z.string().min(1).default('__PENDING_PHASE2_MOCK__'),
    /**
     * PASS client secret — 실 계약 후 KCB 또는 NICE 발급값.
     *
     * @deprecated Plan 02-04 에서 default 허용 — 실 계약 후 제거.
     */
    PASS_CLIENT_SECRET: z.string().min(1).default('__PENDING_PHASE2_MOCK__'),
    /**
     * PASS callback URL — 본 서버의 `/api/v1/kyc/pass/complete` 엔드포인트.
     *
     * Mock 환경: localhost. Prod: https://api.loop.live/...
     */
    PASS_CALLBACK_URL: z
        .string()
        .url()
        .default('http://localhost:4000/api/v1/kyc/pass/complete'),
    // ---- Phase 7 Plan 07-03 — On-demand revalidation (apps/api → apps/web) ----
    /**
     * apps/web 의 `/api/internal/revalidate` base URL.
     *
     * apps/api 가 스트리머 프로필/theme 변경 시 호출 → Next.js 가 `revalidateTag` 로
     * 해당 `/s/{slug}` ISR 캐시를 무효화. 컨테이너 네트워크 내부에서만 도달해야 하며
     * Traefik 외부 라우트로 노출 금지.
     *
     * dev 기본: `http://localhost:3000`. prod: `http://web:3000` (같은 docker network).
     *
     * @since Phase 7 Plan 07-03
     */
    WEB_INTERNAL_URL: z.string().url().default('http://localhost:3000'),
    /**
     * `POST /api/internal/revalidate` 호출 시 사용되는 공유 secret.
     *
     * apps/api 는 `X-Internal-Secret` 헤더에 본 값을 담아 apps/web 에 POST. apps/web Route
     * Handler 는 동일 값과 비교해서 일치하면 `revalidateTag` 실행, 아니면 401. T-07-03-03
     * (Spoofing) 핵심 방어선 — 컨테이너 네트워크 격리와 이중 방어.
     *
     * 최소 32 chars 강제 (HMAC 권장 엔트로피). dev default 는 placeholder — prod 주입 필수.
     * 생성: `openssl rand -hex 32`.
     *
     * @since Phase 7 Plan 07-03
     */
    INTERNAL_REVALIDATE_SECRET: z
        .string()
        .min(32, 'INTERNAL_REVALIDATE_SECRET must be >=32 chars')
        .default('__PENDING_PHASE7_PLAN03_INTERNAL_SECRET___'),
    /**
     * Chat Cockpit JWT HS256 서명 키 — Phase 7 v1.1 / Plan 07-04 (CHAT-03).
     *
     * Capability Token 패턴 — apps/api `GET /api/v1/chat/cockpit-token?streamerId=` 가
     * HS256 JWT 를 발급 (payload=`{sub: user.id, scope: 'chat:join:{streamerId}'}`, exp=30s).
     * apps/realtime `/chat` namespace 의 `chat:join` handler 가 동일 키로 검증.
     *
     * 두 앱이 같은 값을 공유해야 함 (dev 는 `.env` 공용, prod 는 Coolify env 주입).
     *
     * **scope 정책 (T-07-04-06 Elevation)**:
     *   - 발급 경로에 literal `chat:join:{streamerId}` 만 생성. 'write'/'publish'/'send' 등
     *     다른 scope 로 확장하는 경로 **부재** — LOOP→외부 역송출 금지 (T12) 3중 방어 중 하나.
     *
     * 최소 32 chars — HS256 권장 엔트로피. 생성:
     *   `openssl rand -hex 32`  또는  `node -e "console.log(require('crypto').randomBytes(32).toString('hex'))"`.
     *
     * TTL 정책: JWT exp = 30 초 (short-lived Capability Token). revocation 없음 (재사용
     * risk 는 T-07-04-05 accept — 30초 window). Wave 2 에서 jti + Redis replay 방지 검토.
     *
     * @since Phase 7 v1.1 Plan 07-04
     */
    CHAT_COCKPIT_JWT_SECRET: z
        .string()
        .min(32, 'CHAT_COCKPIT_JWT_SECRET must be >=32 chars for HS256')
        .default('__PENDING_PHASE7_PLAN04_CHAT_COCKPIT_SECRET_'),
    /**
     * Plugin Runtime Capability Token HS256 서명 키 — Phase 8 v1.1 / Plan 08-01 (PLUGIN-02).
     *
     * Capability Token 패턴 — apps/api `CapabilityTokenService.sign()` 가 HS256 JWT 를 발급
     * (payload=`{scope: 'plugin:{pluginId}', streamerId, pluginId, maxAmount}`, exp=30s).
     * `PluginSandbox.executeInIsolate()` 진입 시 동일 키로 verify → 실패 시 Unauthorized.
     *
     * 최소 32 chars — HS256 권장 엔트로피. 생성:
     *   `openssl rand -hex 32`  또는  `node -e "console.log(require('crypto').randomBytes(32).toString('hex'))"`.
     *
     * TTL 정책: JWT exp = 30 초 (short-lived Capability Token, T-08-01-01 Spoofing 완화).
     *
     * @since Phase 8 v1.1 Plan 08-01
     */
    PLUGIN_CAPABILITY_JWT_SECRET: z
        .string()
        .min(32, 'PLUGIN_CAPABILITY_JWT_SECRET must be >=32 chars for HS256')
        .default('__PENDING_PHASE8_PLAN01_PLUGIN_CAPTOKEN_SECRET_')
        .refine((v) => v !== '__PENDING_PHASE8_PLAN01_PLUGIN_CAPTOKEN_SECRET_' ||
        process.env.NODE_ENV !== 'production', 'PLUGIN_CAPABILITY_JWT_SECRET must be set to a non-placeholder value in production (HI-03)'),
    /**
     * Plugin Manifest GPG 공개키 (armored) — Phase 8 Plan 08-01 (PLUGIN-05).
     *
     * Wave 1 은 placeholder — 미설정 시 `PluginManifestVerifier.verify()` 는
     * warn 로그만 남기고 서명 검증을 skip 한다 (내부 플러그인 3종만 로드하므로 허용).
     * Wave 2 Widget Store 진입 전 실 서명 필수화 예정 (T-08-01-10 Elevation accept → mitigate).
     *
     * @since Phase 8 v1.1 Plan 08-01
     */
    PLUGIN_GPG_PUBLIC_KEY: z.string().optional(),
    /**
     * Plugin Runtime feature flag — Phase 8 Plan 08-01.
     *
     * Wave 1 기본 OFF. 활성화 시 `PluginRuntimeModule` providers 가 실제로 isolated-vm 을
     * import 한다. 통합 테스트 (`plugin-runtime-penetration.int.test.ts`) 는 본 값이
     * `true` 일 때만 동작 — Windows 로컬 환경에서 isolated-vm native 빌드 실패 시 skip 가능.
     *
     * @since Phase 8 v1.1 Plan 08-01
     */
    PLUGIN_RUNTIME_ENABLED: z.coerce.boolean().default(false),
});
/**
 * Next.js web 앱 전용 env (서버 측).
 * NEXT_PUBLIC_* 은 별도 스키마로 분리하여 브라우저에 노출되는 변수를 명시화.
 *
 * Plan 02-02 확장: Auth.js v5 배선을 위해 AUTH_SECRET · OAuth 4×2 · EMAIL_SERVER_* 5종 ·
 * DATABASE_URL 을 추가. apps/web 은 adapter 주입에 직접 postgres client 가 필요하므로
 * baseEnvSchema.DATABASE_URL 을 그대로 상속하지 않고 명시적으로 선언한다.
 */
export const webServerEnvSchema = baseEnvSchema.pick({ NODE_ENV: true, LOG_LEVEL: true }).extend({
    INTERNAL_API_URL: z.string().url().default('http://localhost:4000'),
    /**
     * apps/web 의 @auth/drizzle-adapter 가 연결할 Postgres URL.
     * apps/api 의 DATABASE_URL 과 동일 DB 를 가리키는 것이 기본 — DB 풀은 프로세스별 분리.
     */
    DATABASE_URL: z.string().url(),
    /**
     * Auth.js v5 세션·CSRF 토큰 서명 키. 최소 32 chars.
     * 생성: `openssl rand -hex 32` 또는
     *   `node -e "console.log(require('crypto').randomBytes(32).toString('hex'))"`.
     *
     * @deprecated Plan 02-02 까지 default (`__PENDING_PHASE2_PLAN02_AUTH_____`) 허용.
     *   prod 주입 필수 — Coolify env 에서 강제.
     */
    AUTH_SECRET: z.string().min(32).default('__PENDING_PHASE2_PLAN02_AUTH_____'),
    // ---- OAuth providers (Plan 02-02) ----
    KAKAO_CLIENT_ID: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    KAKAO_CLIENT_SECRET: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    NAVER_CLIENT_ID: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    NAVER_CLIENT_SECRET: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    GOOGLE_CLIENT_ID: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    GOOGLE_CLIENT_SECRET: z.string().min(1).default('__PENDING_PHASE2_PLAN02__'),
    // ---- Email magic link (dev: Mailhog, prod: Amazon SES SMTP Wave 0 종반) ----
    /** 기본 Mailhog host. */
    EMAIL_SERVER_HOST: z.string().default('localhost'),
    /** 기본 Mailhog SMTP 포트 (1025). */
    EMAIL_SERVER_PORT: z.coerce.number().int().positive().default(1025),
    /** SMTP user (dev 비어있음 허용, prod 필수). */
    EMAIL_SERVER_USER: z.string().default(''),
    /** SMTP password (dev 비어있음 허용, prod 필수). */
    EMAIL_SERVER_PASSWORD: z.string().default(''),
    /** magic link 발신자. dev 는 loopback 도메인 허용. */
    EMAIL_FROM: z.string().default('noreply@loop.test'),
    /**
     * `POST /api/internal/revalidate` 검증용 공유 secret — apps/api 의 동명 env 와 일치.
     *
     * apps/web Route Handler (`/api/internal/revalidate`) 가 `X-Internal-Secret` 헤더
     * 를 본 값과 비교 후 Next.js `revalidateTag('streamer:${slug}')` 실행. 불일치 시 401.
     *
     * 최소 32 chars. prod 주입 필수 — apps/api · apps/web 이 반드시 같은 값을 공유해야 함.
     *
     * @since Phase 7 Plan 07-03
     */
    INTERNAL_REVALIDATE_SECRET: z
        .string()
        .min(32, 'INTERNAL_REVALIDATE_SECRET must be >=32 chars')
        .default('__PENDING_PHASE7_PLAN03_INTERNAL_SECRET___'),
});
export const webPublicEnvSchema = z.object({
    NEXT_PUBLIC_API_URL: z.string().url().default('http://localhost:4000'),
    NEXT_PUBLIC_WIDGET_URL: z.string().url().default('http://localhost:5173'),
    /**
     * Toss Payments 클라이언트 키 — `loadTossPayments(clientKey)` 초기화에 사용.
     * Toss 정책상 브라우저 번들에 포함되어도 안전한 "공개 식별자" 이다 (서버 전용 시크릿 키와 구분).
     * 출처: Toss 개발자센터 > 애플리케이션 > API 키 > 클라이언트 키 (test_ck_* / live_ck_*).
     *
     * @deprecated Phase 4 에서 default 제거 예정 — 실제 결제창 사용 시 반드시 주입.
     */
    NEXT_PUBLIC_TOSS_CLIENT_KEY: z.string().min(1).default('__PENDING_PHASE4__'),
});
/**
 * OBS 위젯 env (전부 빌드 타임 정적 주입).
 */
export const widgetEnvSchema = z.object({
    VITE_API_URL: z.string().url().default('http://localhost:4000'),
    VITE_WS_URL: z.string().url().default('ws://localhost:4000'),
});
/**
 * Realtime Gateway 전용 env (Phase 3 — apps/realtime).
 *
 * Plan 03-01 범위: port + NODE_ENV + LOG_LEVEL 최소셋.
 * Plan 03-02: REDIS_CACHE_URL · REDIS_PUBSUB_URL · REDIS_QUEUE_URL 3 URL required
 *   + `mapLegacyRedisUrl` 로 REDIS_URL legacy alias 복제 지원.
 * Plan 03-03 (현재): DATABASE_URL (read-only session lookup) · AUTH_SECRET
 *   (Auth.js cookie 검증 + widget JWT HS256 서명 placeholder) · WIDGET_JWT_PUBLIC_KEY
 *   (optional — Phase 5 에서 RS256 + JWKS 로 교체 시 공개키 주입).
 *
 * 근거: `.planning/phases/03-realtime-gateway/03-CONTEXT.md` §decisions "Port 4100 (api는 4000)"
 * + ARCHITECTURE.md §3.2 "realtime :4100" + PITFALLS §P8 Redis 3분리 +
 * 03-03 PLAN AUTH_SECRET · DATABASE_URL 추가 계약.
 *
 * baseEnvSchema 의 DATABASE_URL 은 Plan 03-03 에서 auth_sessions · users 조회 (read-only)
 * 용도로 필수 승격. SESSION_SECRET 은 apps/realtime 이 세션 생성/변조 없이 쿠키 검증만 하므로
 * 상속하지 않음.
 */
export const realtimeEnvSchema = baseEnvSchema
    .pick({ NODE_ENV: true, LOG_LEVEL: true })
    .extend({
    REALTIME_PORT: z.coerce.number().int().positive().default(4100),
    /**
     * 3 URL 은 본 스키마에서 required 로 promote (baseEnvSchema 에선 optional).
     * parseEnv 는 `mapLegacyRedisUrl` 로 REDIS_URL → 3 URL 복제 후 이 스키마에 전달.
     */
    REDIS_CACHE_URL: z.string().url(),
    REDIS_PUBSUB_URL: z.string().url(),
    REDIS_QUEUE_URL: z.string().url(),
    /**
     * @since Plan 03-03 — auth_sessions · users read-only lookup.
     *
     * Plan 03-03 의 sessionCookieMiddleware 와 SseController 가 `authjs.session-token`
     * 쿠키 값으로 auth_sessions PK lookup + users SELECT 수행. 쓰기 금지 (ARCHITECTURE §1.3).
     */
    DATABASE_URL: z.string().url(),
    /**
     * @since Plan 03-03 — Auth.js v5 cookie 및 Widget JWT (placeholder) 공통 서명 키.
     *
     * 용도:
     *   1. `/chat`·`/alarm` — Auth.js session cookie 검증 시 DB lookup token 비교
     *      (apps/web 의 AUTH_SECRET 과 동일 값 공유 필수 — 쿠키 동기화).
     *   2. `/widget` + `/sse/widget/:id` — 본 plan 의 placeholder HS256 JWT 서명/검증 키.
     *      Phase 5 에서 RS256 + JWKS 로 교체 시 WIDGET_JWT_PUBLIC_KEY 만으로 검증 (AUTH_SECRET
     *      은 Auth.js cookie 전용으로 축소).
     *
     * min 32 chars — Auth.js v5 요구사항 + 잔여 엔트로피 여유.
     */
    AUTH_SECRET: z.string().min(32),
    /**
     * @since Plan 03-03 — Phase 5 에서 실주입 전까지 optional placeholder.
     *
     * Widget JWT verifier. Wave 0 Plan 03-03: HS256 with AUTH_SECRET (dev · placeholder).
     * Phase 5 에서 RS256 + 별도 `WIDGET_JWT_PUBLIC_KEY` 로 교체 (공개키 검증).
     *
     * 본 필드가 세팅되지 않으면 widgetJwtMiddleware 는 fallback: HS256(AUTH_SECRET).
     * 세팅 시 Phase 5 verifier 가 RS256 + 이 공개키로 검증 (구현은 Phase 5 범위).
     */
    WIDGET_JWT_PUBLIC_KEY: z.string().optional(),
    /**
     * @since Phase 5 Plan 05-01 — Widget JWT HS256 서명 키.
     *
     * apps/api WidgetTokenService 와 동일 값 공유. apps/realtime widgetJwtMiddleware 가
     * 본 키로 jose jwtVerify HS256 검증. AUTH_SECRET 은 더 이상 widget JWT 에 사용하지 않음
     * (cookie 전용으로 축소 — Phase 5 교체 포인트).
     *
     * 최소 32 chars. default __PENDING_PHASE5__ 32-char padding — dev 편의용.
     */
    WIDGET_JWT_SECRET: z
        .string()
        .min(32, 'WIDGET_JWT_SECRET must be >=32 chars for HS256')
        .default('__PENDING_PHASE5_WIDGET_JWT_SECRET_'),
    /**
     * @since Phase 7 v1.1 Plan 07-04 — Chat Cockpit JWT HS256 서명 키.
     *
     * apps/api `chat-cockpit.controller` 가 본 키로 30s JWT 를 서명, apps/realtime 의
     * `/chat` namespace `chat:join` handler 가 동일 키로 검증 (scope=chat:join:{streamerId}
     * + sub=user.id 일치 강제 — T-07-04-01 Spoofing 완화).
     *
     * 두 앱이 같은 값을 공유해야 함 (dev `.env` 공용, prod Coolify env 주입).
     *
     * 최소 32 chars. dev default 는 padding placeholder — prod 반드시 주입.
     */
    CHAT_COCKPIT_JWT_SECRET: z
        .string()
        .min(32, 'CHAT_COCKPIT_JWT_SECRET must be >=32 chars for HS256')
        .default('__PENDING_PHASE7_PLAN04_CHAT_COCKPIT_SECRET_'),
    /**
     * @since Plan 03-04 — Graceful drain 타이머 (SIGTERM 수신 후 server.close 까지의 대기 시간).
     *
     * CONTEXT §decisions 시퀀스:
     *   1) /health/ready → 503 (Traefik routing off)
     *   2) 전 namespace 에 `system:reconnect` broadcast (payload.reconnect_in_ms = 본 값)
     *   3) 본 값만큼 sleep — 클라이언트가 자발적으로 재연결 준비
     *   4) SseBridge stop · socket.io server.close · app.close
     *
     * Default 5000ms (CONTEXT "5초 후 서버 종료"). 테스트에서는 500ms 로 override.
     * Coolify 배포 시 `stop_grace_period >= REALTIME_DRAIN_MS + 5000` 권장.
     */
    REALTIME_DRAIN_MS: z.coerce.number().int().nonnegative().default(5000),
});
/**
 * env 파싱 헬퍼. Zod 에러를 읽기 쉽게 출력 후 process.exit(1).
 *
 * Phase 3 Plan 03-02: schema 적용 전에 `mapLegacyRedisUrl` 로 REDIS_URL → 3 URL 복제 선행.
 * apiEnvSchema·webServerEnvSchema 등 기존 스키마는 REDIS_URL 을 optional 로 유지하므로
 * mapping 자체는 영향 없음 (3 URL 을 optional 로 받거나 아예 무시).
 */
export function parseEnv(schema, source) {
    const mapped = mapLegacyRedisUrl(source);
    const result = schema.safeParse(mapped);
    if (!result.success) {
        process.stderr.write('❌ 환경변수 검증 실패:\n');
        for (const issue of result.error.issues) {
            process.stderr.write(`  - ${issue.path.join('.')}: ${issue.message}\n`);
        }
        process.exit(1);
    }
    return result.data;
}
//# sourceMappingURL=env.js.map